/**
 * Environment variable utilities
 */

// Get environment variable with type safety
export const getEnv = (key: keyof ImportMetaEnv): string => {
  return import.meta.env[key] || '';
};

// Get boolean environment variable
export const getEnvBoolean = (key: keyof ImportMetaEnv): boolean => {
  const value = import.meta.env[key];
  return value === 'true' || value === '1';
};

// Get number environment variable
export const getEnvNumber = (key: keyof ImportMetaEnv): number => {
  const value = import.meta.env[key];
  return parseInt(value, 10) || 0;
};

// Environment helpers
export const env = {
  // App
  appName: getEnv('VITE_APP_NAME'),
  appVersion: getEnv('VITE_APP_VERSION'),
  appEnv: getEnv('VITE_APP_ENV') as 'development' | 'production' | 'staging' | 'uat',
  isDevelopment: getEnv('VITE_APP_ENV') === 'development' || getEnv('VITE_APP_ENV') === 'uat',
  isProduction: getEnv('VITE_APP_ENV') === 'production',
  isStaging: getEnv('VITE_APP_ENV') === 'staging',

  // API
  apiBaseUrl: getEnv('VITE_API_BASE_URL'),
  apiTimeout: getEnvNumber('VITE_API_TIMEOUT'),

  // Shopify
  shopifyCallbackUrl: getEnv('VITE_SHOPIFY_CALLBACK_URL'),
  shopifyTestPayment: getEnvBoolean('VITE_SHOPIFY_TEST_PAYMENT'),

  // Auth
  authTokenKey: getEnv('VITE_AUTH_TOKEN_KEY'),
  authRefreshTokenKey: getEnv('VITE_AUTH_REFRESH_TOKEN_KEY'),
  authEncryptionKey: getEnv('VITE_AUTH_ENCRYPTION_KEY'),

  // Google OAuth
  googleOAuthUrl: getEnv('VITE_GOOGLE_OAUTH_URL'),
  googleClientId: getEnv('VITE_GOOGLE_CLIENT_ID'),
  googleRedirectUri: getEnv('VITE_GOOGLE_REDIRECT_URI'),

  // Firebase
  firebaseApiKey: getEnv('VITE_FIREBASE_API_KEY'),
  firebaseAuthDomain: getEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  firebaseDatabaseUrl: getEnv('VITE_FIREBASE_DATABASE_URL'),
  firebaseProjectId: getEnv('VITE_FIREBASE_PROJECT_ID'),
  firebaseStorageBucket: getEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  firebaseMessagingSenderId: getEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  firebaseAppId: getEnv('VITE_FIREBASE_APP_ID'),
  firebaseMeasurementId: getEnv('VITE_FIREBASE_MEASUREMENT_ID'),

  // Stripe
  stripePublishableKey: getEnv('VITE_STRIPE_PUBLISHABLE_KEY'),
  stripePriceIds: getEnv('VITE_STRIPE_PRICE_IDS'),

  // Third Party
  openaiApiKey: getEnv('VITE_OPENAI_API_KEY'),
  googleAnalyticsId: getEnv('VITE_GOOGLE_ANALYTICS_ID'),
  sentryDsn: getEnv('VITE_SENTRY_DSN'),

  // Features
  enableAnalytics: getEnvBoolean('VITE_ENABLE_ANALYTICS'),
  enableChatHistory: getEnvBoolean('VITE_ENABLE_CHAT_HISTORY'),
  enableMultiLanguage: getEnvBoolean('VITE_ENABLE_MULTI_LANGUAGE'),
  // Integrations matrix (#13) assignment persistence does not exist server-side yet — chatbot-api has
  // the connection lifecycle but nothing recording integration x agent authorisation. With this on,
  // assignments live in localStorage so the matrix is a working screen. Off outside development, so
  // device-local state that would lose a merchant's choices on another device cannot ship.

  // WebSocket
  wsUrl: getEnv('VITE_WS_URL'),
  wsReconnectInterval: getEnvNumber('VITE_WS_RECONNECT_INTERVAL'),

  // Message Hub (Agent Chat)
  wssUrl: getEnv('VITE_WSS_URL'),
  wssPath: getEnv('VITE_PATH'),
  apiKeyEncrypted: getEnv('VITE_API_KEY_ENCRYPTED'),

  // Digital Staff WS chat. chatbot-api bootstrap returns wsBaseUrl, agentId, and chatToken.
  digitalStaffHttp: getEnv('VITE_DIGITAL_STAFF_HTTP'),
  digitalStaffWs: getEnv('VITE_DIGITAL_STAFF_WS'),

  // Storage
  storagePrefix: getEnv('VITE_STORAGE_PREFIX'),

  // Debug
  debugMode: getEnvBoolean('VITE_DEBUG_MODE'),
  logLevel: getEnv('VITE_LOG_LEVEL') as 'debug' | 'info' | 'warn' | 'error',
};

// Logger utility based on environment
export const logger = {
  debug: (...args: unknown[]) => {
    if (env.debugMode && ['debug'].includes(env.logLevel)) {
      console.log('[DEBUG]', ...args);
    }
  },
  info: (...args: unknown[]) => {
    if (['debug', 'info'].includes(env.logLevel)) {
      console.info('[INFO]', ...args);
    }
  },
  warn: (...args: unknown[]) => {
    if (['debug', 'info', 'warn'].includes(env.logLevel)) {
      console.warn('[WARN]', ...args);
    }
  },
  error: (...args: unknown[]) => {
    console.error('[ERROR]', ...args);
  },
};
